import React, { useEffect } from 'react';
import FormSectionHeader from './FormSectionHeader';
import UploadField from './file-upload/UploadField';
import WebsitePreviewField from './file-upload/WebsitePreviewField';
import ImagePreviewModal from './file-upload/ImagePreviewModal';
import { BusinessImageGallery } from './file-upload/BusinessImageGallery';
import { useFileUploader } from './file-upload/useFileUploader';
import { toast } from 'sonner';
import { isUserAdmin } from '@/utils/adminHelper';

interface BusinessFileUploaderProps {
  businessId?: string;
  logoUrl: string;
  pitchDeckUrl: string;
  websitePreviewUrl?: string;
  galleryImages?: {
    id: string;
    url: string;
    caption?: string;
  }[];
  onLogoUpload: (url: string) => void;
  onPitchDeckUpload: (url: string) => void;
  onWebsitePreviewUpload?: (url: string) => void;
  onGalleryImagesChange?: (images: { id: string; url: string; caption?: string; }[]) => void;
}

export const BusinessFileUploader: React.FC<BusinessFileUploaderProps> = ({
  businessId = 'new',
  logoUrl,
  pitchDeckUrl,
  websitePreviewUrl,
  galleryImages = [],
  onLogoUpload,
  onPitchDeckUpload,
  onWebsitePreviewUpload,
  onGalleryImagesChange
}) => {
  const {
    isLogoUploading,
    isPitchDeckUploading,
    isPreviewUploading,
    previewExpanded,
    uploadErrors,
    setPreviewExpanded,
    handleFileUpload,
    handleRemoveFile,
    handleExternalUrl,
    bucketInfo
  } = useFileUploader({
    initialLogoUrl: logoUrl,
    initialPitchDeckUrl: pitchDeckUrl,
    initialWebsitePreviewUrl: websitePreviewUrl,
    onLogoUpload,
    onPitchDeckUpload,
    onWebsitePreviewUpload
  });

  const [isAdmin, setIsAdmin] = React.useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await isUserAdmin();
      setIsAdmin(adminStatus);
    };
    
    checkAdminStatus();
  }, []);

  // Check for storage capabilities when component mounts
  useEffect(() => {
    const checkStorageCapabilities = async () => {
      try {
        if (bucketInfo.checked && bucketInfo.availableBuckets.length === 0) {
          toast.info(
            "Using local storage for images since Supabase storage is not available.", 
            { duration: 6000 }
          );
        }
      } catch (error) {
        console.error('Error checking storage capabilities:', error);
      }
    };
    
    checkStorageCapabilities();
  }, [bucketInfo]);

  return (
    <div className="space-y-6">
      <FormSectionHeader 
        title="Files & Media" 
        description="Upload your business logo, pitch deck, website preview, and gallery images. Images will be stored locally if cloud storage is not available."
      />
      
      <div className="grid gap-6 md:grid-cols-2">
        {/* Logo Upload */}
        <UploadField
          id="logo-upload"
          label="Business Logo"
          fileUrl={logoUrl}
          isUploading={isLogoUploading}
          accept="image/*"
          previewType="image"
          onFileSelect={(e) => handleFileUpload(e, 'logo')}
          onRemove={() => handleRemoveFile('logo')}
          uploadError={uploadErrors.logo}
          onExternalUrl={(url) => handleExternalUrl('logo', url)}
          disableUpload={!isAdmin && bucketInfo.availableBuckets.length > 0}
        />

        {/* Pitch Deck Upload */}
        <UploadField
          id="pitch-deck-upload"
          label="Pitch Deck (PDF/PPT)"
          fileUrl={pitchDeckUrl}
          isUploading={isPitchDeckUploading}
          accept=".pdf,.ppt,.pptx"
          previewType="document"
          onFileSelect={(e) => handleFileUpload(e, 'pitch_deck')}
          onRemove={() => handleRemoveFile('pitch_deck')}
          onViewExternal={() => window.open(pitchDeckUrl, '_blank')}
          uploadError={uploadErrors.pitch_deck}
          onExternalUrl={(url) => handleExternalUrl('pitch_deck', url)}
          disableUpload={!isAdmin && bucketInfo.availableBuckets.length > 0}
        />

        {/* Website Preview Upload */}
        {onWebsitePreviewUpload && (
          <div className="md:col-span-2">
            <WebsitePreviewField
              websitePreviewUrl={websitePreviewUrl || ''}
              isUploading={isPreviewUploading}
              onFileSelect={(e) => handleFileUpload(e, 'website_preview')}
              onRemove={() => handleRemoveFile('website_preview')}
              onExpand={() => setPreviewExpanded(true)}
              uploadError={uploadErrors.website_preview}
              onExternalUrl={(url) => handleExternalUrl('website_preview', url)}
              disableUpload={!isAdmin && bucketInfo.availableBuckets.length > 0}
            />
          </div>
        )}

        {/* Business Gallery */}
        {onGalleryImagesChange && (
          <div className="md:col-span-2">
            <BusinessImageGallery
              businessId={businessId}
              images={galleryImages}
              onImagesChange={onGalleryImagesChange}
              disableUpload={!isAdmin && bucketInfo.availableBuckets.length > 0}
            />
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        imageUrl={websitePreviewUrl || ''}
        isOpen={previewExpanded}
        onClose={() => setPreviewExpanded(false)}
      />
    </div>
  );
};
